import { useReducer } from 'react'
import { Worksheet } from '../../budgetCalculatorModel'
import {
  CompanyFormData,
  SeasonFormData,
  OwnerFormData,
  FieldEmployee,
  AdminEmployee,
  Truck,
  Trailer,
  HeavyMachinery,
  BudgetBuilderState
} from '../types'
import {
  budgetBuilderReducer,
  getInitialState
} from '../budgetBuilderReducer'
import {
  createWorksheetFromBudgetBuilder,
  isStep1Valid,
  isStep2Valid,
  isStep3Valid,
  isStep4Valid,
  isStep5Valid,
  isStep6Valid,
  isStep7Valid,
  isStep8Valid
} from '../budgetBuilderModel'

// Helper function to count items by property value
const countByProperty = <T>(items: T[], property: keyof T, value: any): number => {
  return items.filter(item => item[property] === value).length
}

// Helper function to calculate season length in weeks
const calculateSeasonLength = (startDate: any, endDate: any): number => {
  if (!startDate || !endDate) return 0
  return Math.max(1, endDate.diff(startDate, 'week'))
}

export const useBudgetBuilderState = (createWorksheet: (worksheet: Worksheet, showCongratulations?: boolean) => void, onCancel: () => void): BudgetBuilderState => {
  const [state, dispatch] = useReducer(budgetBuilderReducer, getInitialState())

  // Update functions
  const updateCompanyData = <K extends keyof CompanyFormData>(field: K, value: CompanyFormData[K]) => {
    dispatch({ type: 'UPDATE_COMPANY_DATA', field, value })
  }

  const updateSeasonData = <K extends keyof SeasonFormData>(field: K, value: SeasonFormData[K]) => {
    dispatch({ type: 'UPDATE_SEASON_DATA', field, value })
  }

  const setCrewHoursPerWeek = (hoursPerWeek: number) => {
    dispatch({ type: 'SET_CREW_HOURS_PER_WEEK', hoursPerWeek })
  }

  const updateOwnerData = <K extends keyof OwnerFormData>(field: K, value: OwnerFormData[K]) => {
    dispatch({ type: 'UPDATE_OWNER_DATA', field, value })
  }

  const updateFieldEmployee = (index: number, field: keyof FieldEmployee, value: any) => {
    dispatch({ type: 'UPDATE_FIELD_EMPLOYEE', index, field, value })
  }

  const updateAdminEmployee = (index: number, field: keyof AdminEmployee, value: any) => {
    dispatch({ type: 'UPDATE_ADMIN_EMPLOYEE', index, field, value })
  }

  const updateTruck = (index: number, field: keyof Truck, value: any) => {
    dispatch({ type: 'UPDATE_TRUCK', index, field, value })
  }

  const updateTrailer = (index: number, field: keyof Trailer, value: any) => {
    dispatch({ type: 'UPDATE_TRAILER', index, field, value })
  }

  const updateHeavyMachinery = (index: number, field: keyof HeavyMachinery, value: any) => {
    dispatch({ type: 'UPDATE_HEAVY_MACHINERY', index, field, value })
  }

  const setFieldEmployeeCount = (count: number | null) => {
    dispatch({ type: 'SET_FIELD_EMPLOYEE_COUNT', count })
  }

  const setAdminEmployeeCount = (count: number | null) => {
    dispatch({ type: 'SET_ADMIN_EMPLOYEE_COUNT', count })
  }

  const setTruckCount = (count: number | null) => {
    dispatch({ type: 'SET_TRUCK_COUNT', count })
  }

  const setTrailerCount = (count: number | null) => {
    dispatch({ type: 'SET_TRAILER_COUNT', count })
  }

  const setHeavyMachineryCount = (count: number | null) => {
    dispatch({ type: 'SET_HEAVY_MACHINERY_COUNT', count })
  }

  const addFieldEmployee = () => {
    dispatch({ type: 'ADD_FIELD_EMPLOYEE' })
  }

  const removeFieldEmployee = (index: number) => {
    dispatch({ type: 'REMOVE_FIELD_EMPLOYEE', index })
  }

  const addAdminEmployee = () => {
    dispatch({ type: 'ADD_ADMIN_EMPLOYEE' })
  }

  const removeAdminEmployee = (index: number) => {
    dispatch({ type: 'REMOVE_ADMIN_EMPLOYEE', index })
  }

  const addTruck = () => {
    dispatch({ type: 'ADD_TRUCK' })
  }

  const removeTruck = (index: number) => {
    dispatch({ type: 'REMOVE_TRUCK', index })
  }

  const addTrailer = () => {
    dispatch({ type: 'ADD_TRAILER' })
  }

  const removeTrailer = (index: number) => {
    dispatch({ type: 'REMOVE_TRAILER', index })
  }

  const addHeavyMachinery = () => {
    dispatch({ type: 'ADD_HEAVY_MACHINERY' })
  }

  const removeHeavyMachinery = (index: number) => {
    dispatch({ type: 'REMOVE_HEAVY_MACHINERY', index })
  }

  // Navigation
  const handleBack = () => {
    dispatch({ type: 'PREV_STEP' })
  }

  const handleNext = () => {
    if (state.currentStep === 0) {
      dispatch({ type: 'NEXT_STEP' })
    } else if (state.currentStep === 1 && isStep1Valid(state.companyData)) {
      window.posthog.capture('bb-company-completed', {
        step: 1,
        stepName: 'company',
        salesGoal: state.companyData.salesGoal,
        profitMargin: state.companyData.profitMargin,
        revenueSource: state.companyData.revenueSource,
        seasonalSnowWork: state.companyData.seasonalSnowWork
      })
      dispatch({ type: 'NEXT_STEP' })
    } else if (state.currentStep === 2 && isStep2Valid(state.seasonData)) {
      window.posthog.capture('bb-season-completed', {
        step: 2,
        stepName: 'season',
        daysPerWeek: state.seasonData.daysPerWeek,
        hoursPerWeek: state.seasonData.hoursPerWeek,
        seasonLengthWeeks: calculateSeasonLength(
          state.seasonData.seasonStartDate,
          state.seasonData.seasonEndDate
        )
      })
      dispatch({ type: 'NEXT_STEP' })
    } else if (state.currentStep === 3 && isStep3Valid(state.ownerData)) {
      window.posthog.capture('bb-owner-completed', {
        step: 3,
        stepName: 'owner',
        fieldWorkPercentage: state.ownerData.fieldWorkPercentage,
        hoursPerWeek: state.ownerData.hoursPerWeek
      })
      dispatch({ type: 'NEXT_STEP' })
    } else if (state.currentStep === 4 && isStep4Valid(state.fieldEmployeesData)) {
      window.posthog.capture('bb-field-employees-completed', {
        step: 4,
        stepName: 'field-employees',
        employeeCount: state.fieldEmployeesData.employees.length,
        hourlyCount: countByProperty(state.fieldEmployeesData.employees, 'payType', 'Hourly'),
        salaryCount: countByProperty(state.fieldEmployeesData.employees, 'payType', 'Salary')
      })
      dispatch({ type: 'NEXT_STEP' })
    } else if (state.currentStep === 5 && isStep5Valid(state.adminEmployeesData)) {
      window.posthog.capture('bb-admin-employees-completed', {
        step: 5,
        stepName: 'admin-employees',
        employeeCount: state.adminEmployeesData.employees.length,
        hourlyCount: countByProperty(state.adminEmployeesData.employees, 'payType', 'Hourly'),
        salaryCount: countByProperty(state.adminEmployeesData.employees, 'payType', 'Salary')
      })
      dispatch({ type: 'NEXT_STEP' })
    } else if (state.currentStep === 6 && isStep6Valid(state.trucksData)) {
      window.posthog.capture('bb-trucks-completed', {
        step: 6,
        stepName: 'trucks',
        truckCount: state.trucksData.trucks.length,
        ownedCount: countByProperty(state.trucksData.trucks, 'ownershipType', 'Own'),
        leasedCount: countByProperty(state.trucksData.trucks, 'ownershipType', 'Lease/Finance')
      })
      dispatch({ type: 'NEXT_STEP' })
    } else if (state.currentStep === 7 && isStep7Valid(state.trailersData)) {
      window.posthog.capture('bb-trailers-completed', {
        step: 7,
        stepName: 'trailers',
        trailerCount: state.trailersData.trailers.length,
        ownedCount: countByProperty(state.trailersData.trailers, 'ownershipType', 'Own'),
        leasedCount: countByProperty(state.trailersData.trailers, 'ownershipType', 'Lease/Finance')
      })
      dispatch({ type: 'NEXT_STEP' })
    } else if (state.currentStep === 8 && isStep8Valid(state.heavyMachineryData)) {
      window.posthog.capture('budget-builder-completed', {
        totalSteps: 8,
        salesGoal: state.companyData.salesGoal,
        profitMargin: state.companyData.profitMargin,
        machineryCount: state.heavyMachineryData.machinery.length,
        ownedMachineryCount: countByProperty(state.heavyMachineryData.machinery, 'ownershipType', 'Own'),
        leasedMachineryCount: countByProperty(state.heavyMachineryData.machinery, 'ownershipType', 'Lease/Finance')
      })
      const worksheet = createWorksheetFromBudgetBuilder(
        state.companyData,
        state.seasonData,
        state.ownerData,
        state.fieldEmployeesData,
        state.adminEmployeesData,
        state.trucksData,
        state.trailersData,
        state.heavyMachineryData
      )
      createWorksheet(worksheet, true)
      dispatch({ type: 'RESET' })
      onCancel()
    }
  }

  // Validation
  const isStepValid = (step: number): boolean => {
    switch (step) {
      case 1:
        return isStep1Valid(state.companyData)
      case 2:
        return isStep2Valid(state.seasonData)
      case 3:
        return isStep3Valid(state.ownerData)
      case 4:
        return isStep4Valid(state.fieldEmployeesData)
      case 5:
        return isStep5Valid(state.adminEmployeesData)
      case 6:
        return isStep6Valid(state.trucksData)
      case 7:
        return isStep7Valid(state.trailersData)
      case 8:
        return isStep8Valid(state.heavyMachineryData)
      default:
        return true
    }
  }

  // Worksheet creation
  const createWorksheetFromBuilder = (): Worksheet => {
    return createWorksheetFromBudgetBuilder(
      state.companyData,
      state.seasonData,
      state.ownerData,
      state.fieldEmployeesData,
      state.adminEmployeesData,
      state.trucksData,
      state.trailersData,
      state.heavyMachineryData
    )
  }

  return {
    currentStep: state.currentStep,
    companyData: state.companyData,
    seasonData: state.seasonData,
    ownerData: state.ownerData,
    fieldEmployeesData: state.fieldEmployeesData,
    adminEmployeesData: state.adminEmployeesData,
    trucksData: state.trucksData,
    trailersData: state.trailersData,
    heavyMachineryData: state.heavyMachineryData,
    updateCompanyData,
    updateSeasonData,
    setCrewHoursPerWeek,
    updateOwnerData,
    updateFieldEmployee,
    updateAdminEmployee,
    updateTruck,
    updateTrailer,
    updateHeavyMachinery,
    setFieldEmployeeCount,
    setAdminEmployeeCount,
    setTruckCount,
    setTrailerCount,
    setHeavyMachineryCount,
    addFieldEmployee,
    removeFieldEmployee,
    addAdminEmployee,
    removeAdminEmployee,
    addTruck,
    removeTruck,
    addTrailer,
    removeTrailer,
    addHeavyMachinery,
    removeHeavyMachinery,
    handleNext,
    handleBack,
    isStepValid,
    createWorksheetFromBuilder
  }
}
